// ═══════════════════════════════════════════════════════════════════════════════
// Employer dashboard — glowing arc gauge for today's task completion rate
// Track = silver-gray 270° arc; progress = green glow proportional to %
// ═══════════════════════════════════════════════════════════════════════════════

const CX = 100;
const CY = 71;
const R = 70;
const STROKE = 13;
/** Clockwise from +x (SVG y-down): bottom-left tip → over top → bottom-right tip. */
const ARC_START_DEG = 135;
const ARC_SWEEP_DEG = 270;
const VIEWBOX_MIN_Y = -12;
const VIEWBOX_WIDTH = 200;
const VIEWBOX_HEIGHT = 144;

const SILVER_STROKE = '#94a3b8';
const GREEN_STROKE = '#34d399';

const toRad = (deg: number): number => (deg * Math.PI) / 180;

const arcPoint = (deg: number): { x: number; y: number } => ({
  x: CX + R * Math.cos(toRad(deg)),
  y: CY + R * Math.sin(toRad(deg)),
});

const buildArcPath = (): string => {
  const start = arcPoint(ARC_START_DEG);
  const end = arcPoint(ARC_START_DEG + ARC_SWEEP_DEG);
  const largeArc = ARC_SWEEP_DEG > 180 ? 1 : 0;
  return `M ${start.x} ${start.y} A ${R} ${R} 0 ${largeArc} 1 ${end.x} ${end.y}`;
};

const ARC_PATH = buildArcPath();
const ARC_PATH_LENGTH = 2 * Math.PI * R * (ARC_SWEEP_DEG / 360);

const bottomTip = arcPoint(ARC_START_DEG);
const toOverlayPercent = (viewBoxY: number): string =>
  `${((viewBoxY - VIEWBOX_MIN_Y) / VIEWBOX_HEIGHT) * 100}%`;

/** Text y positions in viewBox units — stacked inside arc opening, date on bottom tips. */
const TEXT_Y_PERCENT = toOverlayPercent(CY - 14);
const TEXT_Y_FRACTION = toOverlayPercent(CY + 6 + VIEWBOX_HEIGHT * 0.1);
const TEXT_Y_FRACTION_MEDIUM = toOverlayPercent(CY + 6 + VIEWBOX_HEIGHT * 0.2);
const TEXT_Y_DATE = toOverlayPercent(bottomTip.y);

const resolvePercentTextClassName = (percent: number): string => {
  if (percent >= 100) {
    return 'text-[2.125rem] font-bold leading-none text-emerald-400 drop-shadow-[0_0_10px_rgba(52,211,153,0.75)] min-[400px]:text-[1.875rem] sm:text-[2.25rem]';
  }
  return 'text-[2.125rem] font-bold leading-none text-emerald-700 min-[400px]:text-[1.875rem] sm:text-[2.25rem] dark:text-emerald-300';
};

type EmployerTodayCompletionArcGaugeProps = {
  percent: number;
  dateYmd: string;
  completionFractionLabel: string;
  completionFractionValue?: string;
  completionFractionStatus?: string;
  completionFractionSubLabel?: string;
};

export const EmployerTodayCompletionArcGauge = ({
  percent,
  dateYmd,
  completionFractionLabel,
  completionFractionValue,
  completionFractionStatus,
  completionFractionSubLabel,
}: EmployerTodayCompletionArcGaugeProps) => {
  const clampedPercent = Math.min(Math.max(percent, 0), 100);
  const progressLength = (ARC_PATH_LENGTH * clampedPercent) / 100;
  const progressGap = Math.max(ARC_PATH_LENGTH - progressLength, 0);
  const showGlow = clampedPercent > 0;
  const hasMediumFractionLayout = Boolean(completionFractionValue && completionFractionStatus);
  const mediumFractionHiddenClass = hasMediumFractionLayout ? 'min-[400px]:hidden sm:flex' : '';
  const mediumFractionSingleHiddenClass = hasMediumFractionLayout ? 'min-[400px]:hidden sm:block' : '';
  const dateHiddenClass = hasMediumFractionLayout ? 'min-[400px]:hidden sm:block' : '';

  return (
    <div className="flex flex-col items-center">
      <div className="relative h-[8rem] w-full max-w-[12.5rem] min-[400px]:h-[7rem] sm:h-[8rem]">
        <svg
          viewBox={`0 ${VIEWBOX_MIN_Y} ${VIEWBOX_WIDTH} ${VIEWBOX_HEIGHT}`}
          className="h-full w-full"
          role="img"
          aria-hidden
        >
          <defs>
            <filter id="employer-completion-arc-glow" x="-50%" y="-50%" width="200%" height="200%">
              <feGaussianBlur stdDeviation="3" result="blur" />
              <feMerge>
                <feMergeNode in="blur" />
                <feMergeNode in="SourceGraphic" />
              </feMerge>
            </filter>
          </defs>
          <path
            d={ARC_PATH}
            fill="none"
            stroke={SILVER_STROKE}
            strokeWidth={STROKE}
            strokeLinecap="round"
          />
          {clampedPercent > 0 ? (
            <path
              d={ARC_PATH}
              fill="none"
              stroke={GREEN_STROKE}
              strokeWidth={STROKE}
              strokeLinecap="round"
              strokeDasharray={`${progressLength} ${progressGap}`}
              filter={showGlow ? 'url(#employer-completion-arc-glow)' : undefined}
              className="transition-all duration-500"
            />
          ) : null}
        </svg>
        <div className="pointer-events-none absolute inset-0">
          <span
            className={`absolute left-1/2 -translate-x-1/2 -translate-y-1/2 whitespace-nowrap ${resolvePercentTextClassName(clampedPercent)}`}
            style={{ top: TEXT_Y_PERCENT }}
          >
            {clampedPercent}%
          </span>
          {hasMediumFractionLayout ? (
            <span
              className="absolute left-1/2 hidden -translate-x-1/2 -translate-y-1/2 flex-col items-center gap-[0.1em] text-center text-sm font-medium leading-none text-gray-500 min-[400px]:flex sm:hidden dark:text-gray-400"
              style={{ top: TEXT_Y_FRACTION_MEDIUM }}
            >
              <span className="whitespace-nowrap text-sm">{completionFractionValue}</span>
              <span className="whitespace-nowrap text-xs leading-none">
                {completionFractionStatus}
              </span>
            </span>
          ) : null}
          {completionFractionSubLabel ? (
            <span
              className={`absolute left-1/2 flex -translate-x-1/2 -translate-y-1/2 flex-col items-center gap-0 text-center text-sm font-medium leading-none text-gray-500 min-[400px]:text-xs sm:text-sm dark:text-gray-400 ${mediumFractionHiddenClass}`}
              style={{ top: TEXT_Y_FRACTION }}
            >
              <span className="whitespace-nowrap">{completionFractionLabel}</span>
              <span className="-mt-0.5 whitespace-nowrap text-xs leading-none min-[400px]:text-[10px] sm:text-xs">
                {completionFractionSubLabel}
              </span>
            </span>
          ) : (
            <span
              className={`absolute left-1/2 -translate-x-1/2 -translate-y-1/2 whitespace-nowrap text-sm font-medium leading-none text-gray-500 min-[400px]:text-xs sm:text-sm dark:text-gray-400 ${mediumFractionSingleHiddenClass}`}
              style={{ top: TEXT_Y_FRACTION }}
            >
              {completionFractionLabel}
            </span>
          )}
          <span
            className={`absolute left-1/2 -translate-x-1/2 -translate-y-1/2 whitespace-nowrap text-xs leading-none text-gray-400 min-[400px]:text-[10px] sm:text-xs dark:text-gray-500 ${dateHiddenClass}`}
            style={{ top: TEXT_Y_DATE }}
          >
            {dateYmd}
          </span>
        </div>
      </div>
      <span className="sr-only">
        {completionFractionSubLabel
          ? `${completionFractionLabel} ${completionFractionSubLabel}`
          : completionFractionLabel}
        , {clampedPercent}%, {dateYmd}
      </span>
    </div>
  );
};
